import React, { useState, useContext, useEffect } from "react";
import styles from "../styles/UserTasksPage.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDownload,
  faStop,
  faRefresh,
  faTasks,
  faCheck,
  faSpinner,
  faExclamationTriangle,
  faCalendarAlt,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import { AuthContext } from "../AuthProvider";
import { 
  getAllTasks, 
  cancelTask, 
  downloadTaskResults,
} from "../api/runner_api";
import { useSnackbar } from "notistack";
import { formatDate } from "../helpers";

const UserTasksPage = () => {
  const { keycloak } = useContext(AuthContext);
  const { enqueueSnackbar } = useSnackbar();
  
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [operationInProgress, setOperationInProgress] = useState(new Set());

  const fetchTasks = async (pageNumber = 1) => {
    setLoading(true);
    try {
      const response = await getAllTasks(keycloak.token, pageNumber, 50);
      setTasks(response.items || []);
      setPage(pageNumber);
      setTotalPages(response.pages || 1);
    } catch (error) {
      console.error("Error fetching runs:", error);
      enqueueSnackbar("Failed to fetch runs. Please check your connection and try again.", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (keycloak.token) {
      fetchTasks();
    }
  }, [keycloak.token]);

  const handleRefresh = () => {
    fetchTasks(page);
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return <FontAwesomeIcon icon={faCheck} className={styles.statusCompleted} />;
      case 'running':
      case 'pending':
        return <FontAwesomeIcon icon={faSpinner} className={styles.statusRunning} spin />;
      case 'cancelled':
      case 'failed':
        return <FontAwesomeIcon icon={faExclamationTriangle} className={styles.statusFailed} />;
      default:
        return <FontAwesomeIcon icon={faSpinner} className={styles.statusRunning} />;
    }
  };

  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return styles.statusCompleted;
      case 'running':
      case 'pending':
        return styles.statusRunning;
      case 'cancelled':
      case 'failed':
        return styles.statusFailed;
      default:
        return styles.statusRunning;
    }
  };

  const canCancel = (status) => {
    return ['running', 'pending', 'waiting_for_input'].includes(status?.toLowerCase());
  };

  const canDownload = (status) => {
    return ['completed'].includes(status?.toLowerCase());
  };

  const handleCancelTask = async (taskId) => {
    setOperationInProgress(prev => new Set([...prev, taskId]));
    try {
      await cancelTask(taskId, keycloak.token);
      enqueueSnackbar("Run cancelled successfully", { variant: "success" });
      fetchTasks(page);
    } catch (error) {
      console.error("Error cancelling run:", error);
      enqueueSnackbar("Failed to cancel run. Please try again.", { variant: "error" });
    } finally {
      setOperationInProgress(prev => {
        const newSet = new Set(prev);
        newSet.delete(taskId);
        return newSet;
      });
    }
  };

  const handleDownloadResults = async (taskId, filename) => {
    setOperationInProgress(prev => new Set([...prev, taskId]));
    try {
      const response = await downloadTaskResults(keycloak.token, taskId);
      
      // Create blob and download
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || `run-${taskId}-results.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      enqueueSnackbar("Download started", { variant: "success" });
    } catch (error) {
      console.error("Error downloading run results:", error);
      enqueueSnackbar("Failed to download run results. Please try again.", { variant: "error" });
    } finally {
      setOperationInProgress(prev => {
        const newSet = new Set(prev);
        newSet.delete(taskId);
        return newSet;
      });
    }
  };

  const formatTaskDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return formatDate(dateString);
    } catch (error) {
      return "Invalid Date";
    }
  };

  if (loading) {
    return (
      <div className={styles.userTasksContainer}>
        <div className={styles.loadingContainer} role="status" aria-live="polite">
          <FontAwesomeIcon icon={faSpinner} spin size="2x" />
          <p>Loading your runs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.userTasksContainer}>
      <div className={styles.header}>
        <h2>
          <FontAwesomeIcon icon={faTasks} /> My Runs
        </h2>
        <div className={styles.headerActions}>
          <button
            className={styles.refreshButton}
            onClick={handleRefresh}
            aria-label="Refresh runs list"
          >
            <FontAwesomeIcon icon={faRefresh} /> Refresh
          </button>
        </div>
      </div>

      {tasks.length === 0 ? (
        <div className={styles.noTasks} role="status" aria-live="polite">
          <FontAwesomeIcon icon={faTasks} size="3x" />
          <h3>No runs found</h3>
          <p>You haven't run any workflows yet. Visit the Workflows page to start running your first workflow.</p>
        </div>
      ) : (
        <>
          <div className={styles.tasksTable} role="table" aria-label="Your workflow runs">
            <div className={styles.tableHeader} role="row">
              <div className={styles.statusColumn} role="columnheader">Status</div>
              <div className={styles.idColumn} role="columnheader">Run ID</div>
              <div className={styles.dateColumn} role="columnheader">
                <FontAwesomeIcon icon={faCalendarAlt} /> Created
              </div>
              <div className={styles.actionsColumn} role="columnheader">Actions</div>
            </div>

            {tasks.map((task) => (
              <div key={task.id} className={styles.taskRow} role="row">
                <div className={styles.statusColumn} role="cell">
                  <span className={`${styles.status} ${getStatusClass(task.status)}`}>
                    {getStatusIcon(task.status)}
                    {task.status || 'Unknown'}
                  </span>
                </div>
                <div className={styles.idColumn} role="cell">
                  <code>{task.id}</code>
                </div>
                <div className={styles.dateColumn} role="cell">
                  {formatTaskDate(task.created_at)}
                </div>
                <div className={styles.actionsColumn} role="cell">
                  {canCancel(task.status) && (
                    <button
                      className={styles.cancelButton}
                      onClick={() => handleCancelTask(task.id)}
                      disabled={operationInProgress.has(task.id)}
                      title="Cancel Run"
                      aria-label={`Cancel run ${task.id}`}
                    >
                      <FontAwesomeIcon icon={faStop} />
                    </button>
                  )}
                  {/* {canDownload(task.status) && (
                    <button
                      className={styles.downloadButton}
                      onClick={() => handleDownloadResults(task.id, `run-${task.id}-results.zip`)}
                      disabled={operationInProgress.has(task.id)}
                      title="Download Run Results"
                      aria-label={`Download results for run ${task.id}`}
                    >
                      <FontAwesomeIcon icon={faDownload} />
                    </button>
                  )} */}
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className={styles.pagination}>
              <button
                className={styles.pageButton}
                onClick={() => fetchTasks(page - 1)}
                disabled={page === 1}
              >
                Previous
              </button>
              <span className={styles.pageInfo}>
                Page {page} of {totalPages}
              </span>
              <button
                className={styles.pageButton}
                onClick={() => fetchTasks(page + 1)}
                disabled={page === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default UserTasksPage;
